import InputField from '../../inputs/inputField'
import CalendarInput from '../../inputs/calendarInput'
import {
	chainIDMapping,
	convertDateToYYYYMMDD,
	socialPlatformMapping,
} from '#app/utils/misc'
import { LoadingIcon } from '../../icons'
import { TypographyParagraph } from '../../typography'
import ArrowExpandIcon from '#app/components/icons/Arrows/ArrowExpandIcon.js'
import debounce from 'lodash.debounce'
import { useMemo, useState } from 'react'
import { type TweetType } from '#app/utils/templates/utils/templateTypes.js'
import { type nft } from '#types/loreTypes.tsx'
import { toast } from 'sonner'
import { Toast } from '../../toastsAndMessages'
import { MetadataProperties } from '#app/server/db.server.js'
import imagePlaceholder from '#images/image-placeholder.jpg'
import imagePlaceholderDark from '#images/image-placeholder-dark.jpg'
import ActionButton from '#app/components/buttons/actionButton.js'
import { useDropzone } from 'react-dropzone-esm'
import PreviewPinImageInputField from '#app/components/inputs/previewPinImageInputField.js'
import Modal from '#app/components/modalsAndOverlays/modal'
import { version } from '#package.json'

export default function TweetForm({
	nft,
	formData,
	selectedForm,
	setFormData,
	requiredFields,
	errorFields,
}: {
	nft?: nft | nft[]
	formData: any
	selectedForm: { name: string; slug: string; icon: null; version: string }
	setFormData: (data: any) => void
	requiredFields: any[]
	errorFields: any[]
}) {
	const [isLoading, setIsLoading] = useState(false)
	const [isUploading, setIsUploading] = useState(false)
	const [isError, setIsError] = useState(false)
	const [hasAttemptedLoad, setHasAttemptedLoad] = useState(
		formData.url ? true : false,
	)
	const [isLoaded, setIsLoaded] = useState(!!formData.url)
	const [isModalOpen, setIsModalOpen] = useState(false)
	const handleDataChange = (name: string, value: string) => {
		let newData = { ...formData }
		newData[name] = value
		setFormData(newData)
		if (name === 'url') {
			debouncedUrlChange(value, newData)
		}
	}

	// Dropzone Setup
	const { getRootProps, getInputProps, open } = useDropzone({
		accept: {
			'image/jpeg': [],
			'image/png': [],
			'image/gif': [],
			'image/svg': [],
		},
		noClick: true,
		maxSize: 9999999,
		onDrop: acceptedFiles => {
			if (acceptedFiles.length) {
				const fileToRender = acceptedFiles[acceptedFiles.length - 1]
				handleMediaScreenshotUpload(fileToRender)
			}
		},
		onDropRejected: fileRejections => {
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						//@ts-ignore
						content={
							'File is not valid, please ensure it is less than 4.5 MB and try again'
						}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
		},
	})

	// State for Screenshot Upload
	// Saving previously uploaded Data from Tweet Post Upload
	const [originalTweetScreenshotData, setOriginalTweetScreenshotData] =
		useState<
			| {
					ipfsCID: string
					arweaveTxId: string
			  }
			| undefined
		>(undefined)

	// URL's for media upload
	const [mediaScreenshotUpload, setMediaScreenshotUpload] = useState<
		| {
				ipfsCID: string
				arweaveTxId: string
				web2URL: string
		  }
		| undefined
	>(undefined)

	// Uploaded File State
	const [currentFile, setCurrentFile] = useState<File | undefined>(undefined)

	const handleOnFileChange = (e: File) => {
		setCurrentFile(e)
		handleMediaScreenshotUpload(e)
	}

	const handleOnFileDelete = () => {
		setCurrentFile(undefined)
		handleDeleteMediaScreenshotUpload()
	}

	// Enter Tweet URL helpers
	const handleUpdateDebouncedUrl = (
		value: string,
		data: MetadataProperties,
	) => {
		if (URL.canParse(value)) {
			const twitterURL = new URL(value)
			if (['x.com', 'twitter.com'].includes(twitterURL.host)) {
				// check if it's a valid post
				const paths = twitterURL.pathname.split('/')
				if (paths.length >= 4) {
					fetchDecentralizedTweetData(value, data)
				}
			}
		}
	}

	const debouncedUrlChange = useMemo(
		() => debounce(handleUpdateDebouncedUrl, 1000),
		[],
	)

	const fetchDecentralizedTweetData = async (
		twitterUrl: string,
		data: MetadataProperties,
	) => {
		setIsLoading(true)
		setHasAttemptedLoad(true)
		toast.custom(
			t => (
				<Toast
					variant={'uploading'}
					//@ts-ignore
					content={'Getting tweet'}
					onClose={() => toast.dismiss(t)}
				/>
			),
			{ position: 'bottom-right' },
		)
		try {
			const tweetImportResponse = await fetch('/importTweetAPI', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
				},
				body: JSON.stringify({
					tweetUrl: twitterUrl,
				}),
			})

			const jsonResponse = (await tweetImportResponse.json()) as {
				success: boolean
				ipfsUrl: string
				arweaveUrl: string
				timestamp: string
				user?: string
				content?: string
			}

			if (jsonResponse.success) {
				let newData = { ...data } as TweetType
				;(newData as TweetType).ipfsCID = jsonResponse.ipfsUrl
				;(newData as TweetType).arweaveTxId = jsonResponse.arweaveUrl
				if (jsonResponse.timestamp) {
					let newTimestamp = jsonResponse.timestamp
					if (newTimestamp.includes(' · ')) {
						const splitTimestamp = newTimestamp.split(' · ')
						if (splitTimestamp[1]) {
							newData.timestamp = new Date(splitTimestamp[1]).toISOString()
						}
					} else {
						;(newData as TweetType).timestamp = jsonResponse.timestamp
					}
				}
				if (jsonResponse.user) {
					;(newData as TweetType).user = jsonResponse.user
				}
				if (jsonResponse.content) {
					;(newData as TweetType).content = jsonResponse.content
				}
				if (currentFile) setCurrentFile(undefined)
				if (mediaScreenshotUpload) setMediaScreenshotUpload(undefined)
				try {
					if (newData?.web2URL) {
						delete newData.web2URL
					}
				} catch (e) {
					console.log('NO web2 URL to overwrite', e)
				}
				setFormData(newData)
				setOriginalTweetScreenshotData({
					ipfsCID: jsonResponse.ipfsUrl,
					arweaveTxId: jsonResponse.arweaveUrl,
				})
				setIsLoading(false)
				setIsLoaded(true)
			} else {
				setIsLoading(false)
				setIsLoaded(true)
				toast.custom(
					t => (
						<Toast
							variant={'error'}
							//@ts-ignore
							content={'An error occured fetching this tweet'}
							onClose={() => toast.dismiss(t)}
						/>
					),
					{ position: 'bottom-right' },
				)
				let newData = { ...data }
				let hasChanged = false
				if (newData.ipfsCID) {
					hasChanged = true
					delete newData.ipfsCID
				}
				if (newData.arweaveTxId) {
					hasChanged = true
					delete newData.arweaveTxId
				}
				if (hasChanged) {
					setFormData(newData)
				}
			}
		} catch (e) {
			setIsLoading(false)
			setIsLoaded(true)
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						//@ts-ignore
						content={'An error occured fetching this tweet'}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
			let newData = { ...formData }
			let hasChanged = false
			if (newData.ipfsCID) {
				hasChanged = true
				delete newData.ipfsCID
			}
			if (newData.arweaveTxId) {
				hasChanged = true
				delete newData.arweaveTxId
			}
			if (hasChanged) {
				setFormData(newData)
			}
		}
	}

	const handleMediaScreenshotUpload = async (file: File) => {
		const screenshotFormData = new FormData()
		screenshotFormData.append('image', file)
		let nftChainID = undefined
		let nftContract = undefined
		let nftTokenID = undefined
		if (nft && !nft.hasOwnProperty('length')) {
			if (nft?.chain && chainIDMapping[nft.chain]) {
				nftChainID = chainIDMapping[nft.chain]
			}
			if (nft.contract && nft.contract.address) {
				nftContract = nft.contract.address
			}
			if (nft.tokenId) {
				nftTokenID = nft.tokenId
			}
		}

		let socialPlatform = undefined
		if (selectedForm.name) {
			// @ts-ignore
			if (socialPlatformMapping[selectedForm.name]) {
				// @ts-ignore
				const mappedValue = socialPlatformMapping[selectedForm.name]
				if (mappedValue) {
					socialPlatform = mappedValue
				}
			}
		}

		let socialUsername = undefined
		if (formData && formData.url && typeof formData.url == 'string') {
			const urlObj = new URL(formData.url)
			if (urlObj.hostname === 'x.com') {
				const urlPathnames = urlObj.pathname.split('/')
				if (urlPathnames.length > 3) {
					if (urlPathnames[2] === 'status') {
						socialUsername = urlPathnames[1]
					}
				}
			}
		}

		const arweaveTags = {
			'App-Name': import.meta.env.VITE_APP_NAME,
			'App-Version': version,
			Type: 'Media',
			Topic: 'Attestations',
			'Content-Type': file.type,
			'AF-Template': selectedForm.name,
			'AF-Template-Version': selectedForm.version,
			...(socialPlatform && { 'Social-Platform': socialPlatform }),
			...(socialUsername && { 'Social-Username': socialUsername }),
			...(import.meta.env.VITE_NODE_ENV &&
				import.meta.env.VITE_NODE_ENV === 'development' && {
					Environment: 'development',
				}),
		}

		screenshotFormData.append('tags', JSON.stringify(arweaveTags))
		try {
			setIsLoading(true)
			setIsUploading(true)
			toast.custom(
				t => (
					<Toast
						variant={'uploading'}
						//@ts-ignore
						content={'Uploading your screenshot, please wait...'}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
			const imageUploadResponse = await fetch('/attestationImageUploadAPI', {
				method: 'POST',
				body: screenshotFormData,
			})
			const imageUploadResponseJson = await imageUploadResponse.json()
			setMediaScreenshotUpload({
				ipfsCID: imageUploadResponseJson.ipfsCID,
				arweaveTxId: imageUploadResponseJson.arweaveTxId,
				web2URL: imageUploadResponseJson.web2URL,
			})
			const newFormData = {
				...formData,
				ipfsCID: imageUploadResponseJson.ipfsCID,
				arweaveTxId: imageUploadResponseJson.arweaveTxId,
				web2URL: imageUploadResponseJson.web2URL,
			}
			setCurrentFile(file)
			setFormData(newFormData)
			setIsLoading(false)
			setIsUploading(false)
			setIsLoaded(true)
			toast.custom(
				t => (
					<Toast
						variant={'success'}
						//@ts-ignore
						content={
							'Successfully uploaded screenshot, please review uploaded image'
						}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
		} catch (e) {
			console.error('ERROR STORING SCREENSHOT UPLOAD', e)
			setIsLoading(false)
			setIsUploading(false)
			setIsLoaded(false)
			toast.custom(
				t => (
					<Toast
						variant={'error'}
						//@ts-ignore
						content={
							'An Error occurred uploading your screenshot, please try again'
						}
						onClose={() => toast.dismiss(t)}
					/>
				),
				{ position: 'bottom-right' },
			)
		}
	}

	const handleDeleteMediaScreenshotUpload = async () => {
		setMediaScreenshotUpload(undefined)
		setCurrentFile(undefined)
		const newFormData = { ...formData }
		if (newFormData.ipfsCID) {
			if (originalTweetScreenshotData?.ipfsCID) {
				newFormData.ipfsCID = originalTweetScreenshotData.ipfsCID
			} else {
				delete newFormData.ipfsCID
			}
		}

		if (newFormData.arweaveTxId) {
			if (originalTweetScreenshotData?.arweaveTxId) {
				newFormData.arweaveTxId = originalTweetScreenshotData.arweaveTxId
			} else {
				delete newFormData.arweaveTxId
			}
		}

		delete newFormData.web2URL

		setFormData(newFormData)
	}

	return (
		<div className="flex w-full flex-col gap-y-2">
			<InputField
				name="tweet url"
				type="text"
				placeholder="https://"
				containerWidth="w-full"
				value={formData.url}
				displayRequired={requiredFields.find(field => field.slug == 'url')}
				onChange={e => {
					handleDataChange('url', e.target.value)
				}}
				helper={'Please enter a valid Tweet url'}
				isError={errorFields.find(requiredField => requiredField.slug == 'url')}
			/>

			{hasAttemptedLoad && (
				<TweetImagePreview
					open={open}
					hasURL={!!formData.url}
					setModalOpen={() => setIsModalOpen(true)}
					getRootProps={getRootProps}
					getInputProps={getInputProps}
					image={formData.ipfsCID}
					isLoaded={isLoaded}
					isLoading={isLoading}
					isUploading={isUploading}
				/>
			)}
			{formData.url && isLoaded && (
				<PreviewPinImageInputField
					onChange={(e: File) => handleOnFileChange(e)}
					onDelete={() => handleOnFileDelete()}
					value={
						mediaScreenshotUpload && mediaScreenshotUpload.ipfsCID
							? mediaScreenshotUpload.ipfsCID
							: undefined
					}
					currentFile={currentFile}
					label={'Having trouble with the screenshot?'}
					helper={`No worries! You can upload a jpg, png, or gif, and we'll store it to Web3 Storage and add the Web3 version to the attestation. Files must be < 4.5 MB`}
					isUploading={isUploading}
				/>
			)}
			<CalendarInput
				name="tweet date"
				containerWidth="w-full"
				value={
					formData.timestamp
						? convertDateToYYYYMMDD(new Date(formData.timestamp))
						: undefined
				}
				maxDate={convertDateToYYYYMMDD(new Date())}
				displayRequired={requiredFields.find(
					field => field.slug == 'timestamp',
				)}
				onChange={e => {
					const timestampString = new Date(e.target.value).toISOString()
					handleDataChange('timestamp', timestampString)
				}}
				isError={errorFields.find(
					requiredField => requiredField.slug == 'timestamp',
				)}
				helper={"Select the tweet's date"}
			/>
			<InputField
				name="comment"
				lines={4}
				placeholder="Add context to this offchain history signature"
				containerWidth="w-full"
				additionalClasses="h-32"
				value={formData.description}
				displayRequired={requiredFields.find(
					field => field.slug == 'description',
				)}
				onChange={e => {
					handleDataChange('description', e.target.value)
				}}
				isError={errorFields.find(
					requiredField => requiredField.slug == 'description',
				)}
			/>
			{/*
			Leaving this here in case we need to make these fields editable
			<InputField
				name="user"
				placeholder="Twitter user's handle"
				containerWidth="w-full"
				value={formData.user}
				displayRequired={requiredFields.find(
					field => field.slug == 'user',
				)}
				onChange={e => {
					handleDataChange('user', e.target.value)
				}}
				isError={errorFields.find(
					requiredField => requiredField.slug == 'user',
				)}
			/>
			<InputField
				name="content"
				lines={4}
				placeholder="Tweet's text content"
				containerWidth="w-full"
				additionalClasses="h-32"
				value={formData.content}
				displayRequired={requiredFields.find(
					field => field.slug == 'content',
				)}
				onChange={e => {
					handleDataChange('content', e.target.value)
				}}
				isError={errorFields.find(
					requiredField => requiredField.slug == 'content',
				)}
			/>*/}
			<Modal
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				body={
					<div>
						<img src={formData.ipfsCID} alt="tweet preview" />
					</div>
				}
			/>
		</div>
	)
}

const TweetImagePreview = ({
	open,
	hasURL,
	setModalOpen,
	getRootProps,
	getInputProps,
	image,
	isLoaded,
	isLoading,
	isUploading,
}: {
	open: () => void
	hasURL: boolean
	setModalOpen: () => void
	getRootProps: (props?: { className: string }) => { className: string }
	getInputProps: any
	image: string
	isLoaded: boolean
	isLoading: boolean
	isUploading: boolean
}) => {
	return (
		<div className="w-full rounded-md border border-uiPrimaryLight dark:border-uiPrimaryDark">
			{isLoading ? (
				<div className="my-8 flex h-full w-full flex-col items-center justify-center">
					<div className="animate-spin">
						<LoadingIcon />
					</div>
					<TypographyParagraph size="medium">
						{isUploading
							? 'Capturing Uploaded Image'
							: 'Capturing Tweet Screenshot...'}
					</TypographyParagraph>
				</div>
			) : image ? (
				<div className="relative h-[238px] w-full">
					<div
						className="absolute right-2 top-2 cursor-pointer bg-uiSecondaryLight dark:bg-uiSecondaryDark"
						onClick={() => setModalOpen()}
					>
						<ArrowExpandIcon />
					</div>
					<img
						src={image}
						alt="tweet-preview"
						className="mx-auto h-[238px] object-contain"
					/>
				</div>
			) : (
				<div className="relative h-[238px] w-full">
					{hasURL && !isLoading && (
						<div
							{...getRootProps({
								className: 'absolute cursor-pointer right-2 top-2',
							})}
						>
							<input {...getInputProps()} />
							<div onClick={() => open()}>
								<ActionButton size="small" type="plus" />
							</div>
						</div>
					)}
					<img
						src={imagePlaceholderDark}
						alt="tweet-preview"
						className="mx-auto hidden h-full object-contain dark:block"
					/>
					<img
						src={imagePlaceholder}
						alt="tweet-preview"
						className="mx-auto block h-full object-contain dark:hidden"
					/>
				</div>
			)}
		</div>
	)
}
